import React, { FC, ReactNode } from "react";
import { Accordion, AccordionSummary, AccordionDetails, Typography, Grid, Box, Alert, Skeleton } from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import createStyles from "@mui/styles/createStyles";
import { ExpandMore } from "@mui/icons-material";
import { verbalTheme } from "../../layout/themes/verbal.theme";
import { ContentEditor } from "../../common/ContentEditor";
import { PrepareQuestionModule } from "./questions/PrepareQuestionModule";
import { PrimaryButton } from "../../layout/Button/Button";
import { VTooltip } from "../../../core/components/VTooltip";
import { MappedStoryPartType } from "../../../models/modelTypes";

const useStyles = makeStyles(() =>
    createStyles({
        accordion: {
            marginBottom: "16px!important",
        },
        linearAccordion: {
            padding: "0 16px 0 16px",
            backgroundColor: "rgba(0,0,0,0.05)",
        },
        branchedAccordion: {
            border: "1px solid #e0e0e0",
            backgroundColor: "transparent",
        },
        accordionHeader: {
            minHeight: "64px",
        },
        partNumber: {
            backgroundColor: "#121738",
            color: "#ffffff",
            borderRadius: "50%",
            width: "32px",
            height: "32px",
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: "14px",
            fontWeight: "bold",
            marginLeft: "12px",
            marginRight: "12px",
        },
    }),
);

interface StoryPartAnnotationProps {
    partNumber: number;
    totalParts: number;
    isExpanded: boolean;
    onToggleExpand: (isExpanded: boolean) => void;
    isQuestionsOpen: boolean;
    onToggleQuestions: (isOpen: boolean) => void;

    // Content Editor Props
    initialContent: string;
    mappedStoryPartId: number;
    content: Map<number, string>;
    setContent: (id: number, content: string) => void;
    setIsEdited: (edited: boolean) => void;
    setIsEditorLoaded: (loaded: boolean) => void;
    isEditorLoaded: boolean;

    // Questions Props
    mappedStoryPart?: MappedStoryPartType;
    mappedStoryId?: number;

    // Image Preview Props
    imageUrl?: string;
    isLoadingImage?: boolean;
    onImageClick?: (url: string) => void;
    onLoadImage?: () => void;

    // Layout/UI Props
    isMobile: boolean;
    isEndPoint?: boolean;
    extraContent?: ReactNode; // e.g. Decision Points
    onBackToTop?: () => void;

    // Tour/Refs
    showQuestionsRef?: React.RefObject<HTMLButtonElement>;
    hideQuestionsRef?: React.RefObject<HTMLButtonElement>;
    accordionRef?: (el: HTMLDivElement | null) => void;

    // Data Tour Tags
    isFirstPart?: boolean;
    tooltipOpen?: boolean;
    tourStateOpen?: boolean;
}

export const StoryPartAnnotation: FC<StoryPartAnnotationProps> = ({
    partNumber,
    totalParts,
    isExpanded,
    onToggleExpand,
    isQuestionsOpen,
    onToggleQuestions,
    initialContent,
    mappedStoryPartId,
    content,
    setContent,
    setIsEdited,
    setIsEditorLoaded,
    isEditorLoaded,
    mappedStoryPart,
    mappedStoryId,
    imageUrl,
    isLoadingImage,
    onImageClick,
    onLoadImage,
    isMobile,
    isEndPoint,
    extraContent,
    onBackToTop,
    showQuestionsRef,
    hideQuestionsRef,
    accordionRef,
    isFirstPart,
    tooltipOpen,
    tourStateOpen,
}) => {
    const classes = useStyles();

    // Original visibility logic: marginTop ONLY when visible
    const visibilityStyle = isEditorLoaded
        ? { visibility: "visible" as const, marginTop: 5 }
        : { visibility: "hidden" as const };

    return (
        <div
            ref={(el) => {
                if (accordionRef) accordionRef(el);
            }}
            style={visibilityStyle}
        >
            <Accordion
                className={`${classes.accordion} ${classes.branchedAccordion}`}
                expanded={isExpanded}
                onChange={(_event, expanded) => onToggleExpand(expanded)}
                elevation={0}
            >
                <AccordionSummary
                    className={classes.accordionHeader}
                    expandIcon={<ExpandMore data-tour={isFirstPart ? "prep-part-accord" : undefined} />}
                >
                    <Box
                        display="flex"
                        alignItems="center"
                        width="100%"
                        data-tour={isFirstPart ? "prep-story-part-num" : undefined}
                    >
                        <span className={classes.partNumber}>{partNumber}</span>
                        <Box flexGrow={1}>
                            <Typography style={{ fontWeight: "bold" }}>
                                Part {partNumber} of {totalParts}
                                {extraContent && " • Decision Point"}
                                {isEndPoint && " • End Point"}
                            </Typography>
                        </Box>
                    </Box>
                </AccordionSummary>
                <AccordionDetails
                    style={{
                        height: "100%",
                        padding: "0px",
                        paddingBottom: verbalTheme.spacing(2),
                    }}
                >
                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            <Box px={2}>
                                <Alert
                                    data-tour={isFirstPart ? "prep-story-alert" : undefined}
                                    severity="warning"
                                    variant="filled"
                                    style={{ fontWeight: "bold" }}
                                >
                                    Please read through part {partNumber} before reviewing the part {partNumber}{" "}
                                    questions
                                </Alert>
                            </Box>
                        </Grid>

                        <Grid item xs={12} sm={12} md={isQuestionsOpen ? 6 : 10}>
                            <VTooltip
                                title={
                                    isFirstPart
                                        ? "You can use the text tools to highlight, bold, underline and/or italicise the text of the story to help you prepare"
                                        : ""
                                }
                                placement="top-start"
                                open={!tourStateOpen && tooltipOpen}
                            >
                                <span data-tour={isFirstPart ? "prep-story-content" : undefined}>
                                    <ContentEditor
                                        setIsEditorLoaded={setIsEditorLoaded}
                                        setIsEdited={setIsEdited}
                                        content={content}
                                        setContent={setContent}
                                        initialContent={initialContent}
                                        mappedStoryPartId={mappedStoryPartId}
                                        id={`prepare-part-${partNumber}`}
                                    />
                                </span>
                            </VTooltip>
                            {!isMobile && (
                                <Typography
                                    color="error"
                                    style={{
                                        fontWeight: "bold",
                                        marginTop: "8px",
                                        marginLeft: "16px",
                                    }}
                                >
                                    End of part {partNumber} go to show part {partNumber} questions {">>>"}
                                </Typography>
                            )}
                            {extraContent}
                        </Grid>

                        <Grid item xs={12} sm={12} md={isQuestionsOpen ? 6 : 2} pr={2}>
                            {isQuestionsOpen || isMobile ? (
                                <>
                                    {mappedStoryPart && (
                                        <PrepareQuestionModule part={mappedStoryPart} mappedStoryId={mappedStoryId} />
                                    )}
                                    {!isMobile && (
                                        <Box mt={3}>
                                            <VTooltip title={`Hide Part ${partNumber} Questions`}>
                                                <PrimaryButton
                                                    ref={hideQuestionsRef}
                                                    onClick={() => onToggleQuestions(false)}
                                                    style={{ fontWeight: "bold" }}
                                                >
                                                    Hide part {partNumber} questions
                                                </PrimaryButton>
                                            </VTooltip>
                                        </Box>
                                    )}
                                </>
                            ) : (
                                <Box
                                    display="flex"
                                    flexDirection={"column"}
                                    justifyContent={"space-between"}
                                    height={"100%"}
                                >
                                    <Box width={"100%"} display="flex" justifyContent="center" mt={1}>
                                        <VTooltip title={`Show Part ${partNumber} Questions`}>
                                            <PrimaryButton
                                                ref={showQuestionsRef}
                                                data-tour={isFirstPart ? "prep-question-show" : undefined}
                                                onClick={() => onToggleQuestions(true)}
                                                style={{ fontWeight: "bold" }}
                                            >
                                                Show part {partNumber} questions
                                            </PrimaryButton>
                                        </VTooltip>
                                    </Box>
                                    {imageUrl && (
                                        <Box width={"100%"} mt={5} sx={{ flexGrow: 1 }}>
                                            {isLoadingImage ? (
                                                <Skeleton
                                                    variant="rectangular"
                                                    animation="wave"
                                                    width={"100%"}
                                                    height={168}
                                                />
                                            ) : (
                                                <div>
                                                    <img
                                                        src={imageUrl}
                                                        style={{ cursor: "pointer", maxWidth: "100%" }}
                                                        onClick={() => onImageClick?.(imageUrl)}
                                                        onLoad={onLoadImage}
                                                    />
                                                    <Typography align="center">Image Preview</Typography>
                                                </div>
                                            )}
                                        </Box>
                                    )}
                                    {onBackToTop && (
                                        <Box width={"100%"}>
                                            <PrimaryButton onClick={onBackToTop} style={{ fontWeight: "bold" }}>
                                                Back to part {partNumber} top
                                            </PrimaryButton>
                                        </Box>
                                    )}
                                </Box>
                            )}
                        </Grid>
                    </Grid>
                </AccordionDetails>
            </Accordion>
        </div>
    );
};
